import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES } from "../content/defaultContent";
import { Reveal } from "../components/motion/Reveal";
import { TextReveal } from "../components/motion/TextReveal";
import { Counter } from "../components/motion/Counter";
import { Overline, SectionTitle } from "../components/blocks";
import { CommodityCard } from "../components/CommodityCard";

const Marquee = ({ words }) => (
  <div className="relative overflow-hidden border-y border-brand-green/10 bg-brand-beige py-8">
    <div className="flex w-max animate-marquee whitespace-nowrap">
      {[...words, ...words].map((w, i) => (
        <span
          key={i}
          className="mx-8 font-serif text-4xl font-light italic text-brand-green/25 md:text-6xl"
        >
          {w} <span className="text-brand-gold">✦</span>
        </span>
      ))}
    </div>
  </div>
);

export default function Home() {
  const { content } = useLanguage();
  const h = content.home;
  const ui = content.ui;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);

  const marqueeWords = content.commodities.map((c) => c.name.split(" ")[0]).concat([
    "Export",
    "Traçabilité",
    "Congo",
  ]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
          <img
            src={IMAGES.hero}
            alt="Bassin du Congo"
            className="h-full w-full object-cover"
            loading="eager"
          />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-green/60 via-transparent to-black/30" />

        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-6 md:px-12">
          <Reveal variant="fade" delay={0.2} className="mb-6 text-xs uppercase tracking-[0.3em] text-brand-gold">
            {h.hero.overline}
          </Reveal>
          <h1 className="max-w-5xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-white md:text-7xl lg:text-[5.5rem]">
            <TextReveal text={h.hero.title} delay={0.3} stagger={0.08} />
          </h1>
          <Reveal variant="up" delay={0.7} className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-white/80 md:text-xl">
            {h.hero.subtitle}
          </Reveal>
          <Reveal variant="up" delay={0.9} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link
              to="/matieres-premieres"
              data-testid="hero-discover-btn"
              className="rounded-none bg-brand-gold px-8 py-4 text-center text-xs uppercase tracking-[0.15em] text-brand-green transition-colors duration-300 hover:bg-white"
            >
              {ui.buttons.discover}
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="rounded-none border border-white px-8 py-4 text-center text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-white hover:text-brand-green"
            >
              {ui.buttons.contact}
            </Link>
          </Reveal>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2 text-white/60"
        >
          <ArrowDown className="h-5 w-5 animate-bounce" />
        </motion.div>
      </section>

      {/* PRESENTATION */}
      <section data-testid="presentation-section" className="bg-brand-beige py-24 md:py-36">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-4">
            <Reveal variant="left">
              <Overline>{h.presentation.overline}</Overline>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <SectionTitle className="mb-8">
              <TextReveal text={h.presentation.title} />
            </SectionTitle>
            <Reveal variant="up" delay={0.15} className="max-w-3xl text-lg leading-relaxed text-brand-black/75">
              {h.presentation.body}
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee words={marqueeWords} />

      {/* VALUES */}
      <section data-testid="values-section" className="bg-white py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal variant="up" className="mb-4">
            <Overline>{h.values.overline}</Overline>
          </Reveal>
          <SectionTitle className="mb-16 max-w-3xl">{h.values.title}</SectionTitle>
          <div className="grid grid-cols-1 gap-px overflow-hidden border border-brand-grey bg-brand-grey md:grid-cols-2 lg:grid-cols-4">
            {h.values.items.map((v, i) => (
              <Reveal
                key={i}
                variant="up"
                delay={i * 0.1}
                className="group bg-white p-10 transition-colors duration-500 hover:bg-brand-green"
              >
                <span className="font-mono text-sm text-brand-gold">0{i + 1}</span>
                <h3 className="mt-6 font-serif text-2xl text-brand-green transition-colors group-hover:text-white">
                  {v.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-brand-black/65 transition-colors group-hover:text-white/70">
                  {v.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section data-testid="commitments-section" className="bg-brand-beige py-24 md:py-36">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-14 px-6 md:grid-cols-2 md:px-12">
          <Reveal variant="left" className="relative overflow-hidden">
            <img src={IMAGES.b2b} alt="" className="h-[60vh] w-full object-cover" loading="lazy" />
          </Reveal>
          <div>
            <Reveal variant="up" className="mb-4">
              <Overline>{h.commitments.overline}</Overline>
            </Reveal>
            <SectionTitle className="mb-8">{h.commitments.title}</SectionTitle>
            <Reveal variant="up" delay={0.15} className="text-lg leading-relaxed text-brand-black/75">
              {h.commitments.body}
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="bg-brand-green py-24 text-white md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal variant="up" className="mb-4">
            <Overline>{h.stats.overline}</Overline>
          </Reveal>
          <SectionTitle light className="mb-16 max-w-3xl">
            {h.stats.title}
          </SectionTitle>
          <div className="grid grid-cols-2 gap-10 lg:grid-cols-4">
            {h.stats.items.map((s, i) => (
              <Reveal key={i} variant="up" delay={i * 0.1}>
                <div className="font-serif text-6xl font-light text-brand-gold md:text-7xl">
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <p className="mt-4 text-sm uppercase tracking-[0.12em] text-white/70">
                  {s.label}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMODITIES */}
      <section data-testid="commodities-preview-section" className="bg-white py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Reveal variant="up" className="mb-4">
                <Overline>{h.commoditiesSection.overline}</Overline>
              </Reveal>
              <SectionTitle className="max-w-2xl">{h.commoditiesSection.title}</SectionTitle>
            </div>
            <Reveal variant="up" className="max-w-sm text-base text-brand-black/65">
              {h.commoditiesSection.body}
            </Reveal>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {content.commodities.map((item, i) => (
              <Reveal key={item.slug} variant="up" delay={i * 0.12}>
                <CommodityCard item={item} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section data-testid="why-preview-section" className="bg-brand-beige py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal variant="up" className="mb-4">
            <Overline>{h.whyChoose.overline}</Overline>
          </Reveal>
          <SectionTitle className="mb-16 max-w-3xl">{h.whyChoose.title}</SectionTitle>
          <div className="grid grid-cols-1 gap-x-16 gap-y-12 md:grid-cols-2">
            {h.whyChoose.items.map((r, i) => (
              <Reveal key={i} variant="up" delay={i * 0.08} className="flex gap-6 border-t border-brand-green/15 pt-8">
                <span className="font-mono text-sm text-brand-gold">0{i + 1}</span>
                <div>
                  <h3 className="font-serif text-2xl text-brand-green">{r.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-brand-black/70">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MAP */}
      <section data-testid="map-section" className="relative overflow-hidden bg-brand-green py-24 text-white md:py-36">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-16 px-6 md:grid-cols-2 md:px-12">
          <div>
            <Overline>{h.map.overline}</Overline>
            <SectionTitle light className="mb-8 mt-4">{h.map.title}</SectionTitle>
            <p className="max-w-lg text-lg leading-relaxed text-white/75">{h.map.body}</p>
            <div className="mt-10 space-y-5">
              {h.map.countries.map((c, i) => (
                <Reveal key={i} variant="left" delay={i * 0.1} className="flex items-center gap-4 border-b border-white/10 pb-4">
                  <MapPin className="h-5 w-5 text-brand-gold" />
                  <div>
                    <p className="font-serif text-2xl">{c.name}</p>
                    <p className="text-sm text-white/60">{c.city}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal variant="right" className="relative aspect-square">
            <div className="absolute inset-0 rounded-full border border-brand-gold/20" />
            <div className="absolute inset-8 rounded-full border border-brand-gold/15" />
            <div className="absolute inset-16 rounded-full border border-brand-gold/10" />
            <div className="absolute inset-24 rounded-full border border-brand-gold/10" />
            <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-gold shadow-[0_0_30px_8px_rgba(184,156,93,0.5)]" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="font-mono text-xs tracking-[0.3em] text-brand-gold">4°16′ S · 15°17′ E</span>
              <span className="mt-4 font-serif text-5xl font-light text-white md:text-6xl">Congo</span>
              <span className="mt-3 flex gap-3 font-mono text-sm text-white/50">
                <span>CG</span>
                <span className="text-brand-gold">/</span>
                <span>CD</span>
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPORT */}
      <section data-testid="export-section" className="relative overflow-hidden py-32 md:py-44">
        <div className="absolute inset-0">
          <img src={IMAGES.export} alt="" className="h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-xl border border-white/15 bg-brand-green/85 p-10 backdrop-blur-md md:p-14">
            <Overline>{h.exportSection.overline}</Overline>
            <SectionTitle light className="mb-6 mt-4">{h.exportSection.title}</SectionTitle>
            <p className="text-lg leading-relaxed text-white/80">{h.exportSection.body}</p>
            <Link
              to="/exportation"
              data-testid="export-section-link"
              className="mt-10 inline-block rounded-none border border-brand-gold px-8 py-4 text-xs uppercase tracking-[0.15em] text-brand-gold transition-colors hover:bg-brand-gold hover:text-brand-green"
            >
              {ui.nav.export}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="bg-brand-beige py-28 md:py-40">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
          <SectionTitle className="mx-auto mb-6 max-w-3xl">
            <TextReveal text={h.cta.title} />
          </SectionTitle>
          <Reveal variant="up" delay={0.15} className="mx-auto mb-10 max-w-xl text-lg text-brand-black/70">
            {h.cta.body}
          </Reveal>
          <Reveal variant="up" delay={0.25}>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-block rounded-none bg-brand-green px-10 py-5 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-brand-black"
            >
              {h.cta.button}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
